// Which `intake` blanks a client can actually answer today.
//
// CONSUMER: shared (the only-what-you-know screen, and whatever wires an
// intake template to a form's blanks once one is wired).
//
// ── WHAT AN INTAKE RULE PROMISES ────────────────────────────────────
//
// `intake` says: this blank is the CLIENT's answer, arriving through the
// forms platform. The seller says whether the roof leaks; the buyer says
// which lender they are using. Nobody at Stewardship types it, and the
// preparer is right not to be asked.
//
// That is a promise with two halves, and only-what-you-know has always
// kept the first: it leaves these off the question list. The second half
// — that an intake form exists, is sent, and writes its answers onto
// this blank — has never been true for a single row.
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────
//
// Measured 2026-09-18, before writing a line of it: the Realty atlas
// carries 669 `intake` rows and NOT ONE intake template is wired to any
// of them. The reconciliation document says so in as many words ("what
// remains is wiring the SPDS's blanks to an intake template"). Every one
// of those 669 was counted in the "the OS filled N blanks" line the
// short form leads with.
//
// 517 are the Seller's Property Disclosure Statement, which Nate does in
// Breeze today, so nobody has been hurt by those. The other 152 are on
// forms that go out: 34 on the H.O.A. addendum, 19 on the listing
// contract, 6 on the Lead-Based Paint Disclosure. On an H.O.A. addendum
// that is 34 of 70 blanks claimed as handled by an OS that has never had
// anywhere to put them.
//
// ── WHY THESE ARE NOT SIMPLY "ASKED" ────────────────────────────────
//
// This is the one place where the derive fix's answer would be wrong.
// An unimplemented derive rule is asked, because a calculation nobody
// wrote and a blank with no home are the same thing FROM THE SENDER'S
// CHAIR — either way it is theirs to fill. An unwired intake blank is
// not: "does the roof leak?" is not Nate's answer to give, and putting
// 276 of the seller's disclosures on his question list would be a worse
// lie than the one it replaced, and a slower one.
//
// So an unwired intake blank gets the third answer, which is the true
// one: nobody has filled this, it is the client's to answer, and there
// is no form asking them yet. It is listed, it is not counted as filled,
// and it is not counted as the sender's job.
//
// ── ADDING ONE ──────────────────────────────────────────────────────
//
// Land the entry in the SAME change that wires the intake template to
// the blank. An entry here with no form behind it re-creates exactly the
// defect this file was written to end, and does it invisibly, because
// the blank goes straight back to being counted as filled.

/** The intake targets an intake form actually asks a client today.
 *
 *  Keyed by the fill rule's own ref — the reviewer's words for where the
 *  answer comes from — normalized the same way `deriveRuleName` does.
 *
 *  EMPTY, and that is the honest state (2026-09-18). No intake template
 *  is wired to any form blank in this codebase. */
export const WIRED_INTAKE_TARGETS: ReadonlySet<string> = new Set<string>();

/** True when a client is genuinely going to be asked this, so the blank
 *  really does fill itself and the preparer is right not to see it. */
export function intakeTargetIsWired(ref: string | null | undefined): boolean {
  if (typeof ref !== "string") return false;
  const name = ref.trim().toLowerCase();
  return name.length > 0 && WIRED_INTAKE_TARGETS.has(name);
}
